import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { addPatient, } from "../../../Redux/PatientSlice";
import { addPatientAllergy } from "../../../Redux/AllergySlice";
import { toast } from "react-toastify";
import Breadcrumb from "../../dataUi/Breadcrumb";
import dayjs from 'dayjs';

const AddPatient = () => {
  const [patientInfo, setPatientInfo] = useState({
    firstName: "",
    lastName: "",
    dob: "",
    gender: "",
    address: "",
    phone: "",
    idType: "",
    idNumber: "",
  });
  const [allergies, setAllergies] = useState([
    {
      name: "",
      reaction: "",
      onset_date: "",
    },
  ]);
  const [noKnownAllergies, setNoKnownAllergies] = useState(false);
  const [age, setAge] = useState('');
  const [allergyYears, setAllergyYears] = useState([]);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const handlePatientInfoChange = (field, value, onError) => {
    if (field === "dob") {
      const selectedDate = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate > today) {
        if (onError) toast.error(onError);
        return;
      }
    }

    setPatientInfo({ ...patientInfo, [field]: value });
  };

  const handleAllergyChange = (index, field, value) => {
    const updatedAllergies = [...allergies];
    updatedAllergies[index][field] = value;
    setAllergies(updatedAllergies);
  };

  const addAllergy = () => {
    setAllergies([
      ...allergies,
      {
        name: "",
        reaction: "",
        onset_date: "",
      },
    ]);
  };

  const removeAllergy = (index) => {
    if (allergies.length > 1) {
      const updatedAllergies = [...allergies];
      updatedAllergies.splice(index, 1);
      setAllergies(updatedAllergies);
    }
  };

  const isPatientInfoFilled = Object.values(patientInfo).every(
    (value) => value !== ""
  );
  const isAllergiesFilled =
    noKnownAllergies ||
    allergies.every(
      (allergy) =>
        allergy.name !== "" &&
        allergy.reaction !== "" &&
        allergy.onset_date !== ""
    );

  const handleNoKnownAllergiesChange = (e) => {
    setNoKnownAllergies(e.target.checked);
  };

  // Calculate age when dob changes
  useEffect(() => {
    if (patientInfo.dob) {
      const birthDate = dayjs(patientInfo.dob);
      const today = dayjs();
      let calculatedAge = today.diff(birthDate, 'year');
      setAge(isNaN(calculatedAge) ? '' : calculatedAge);
    } else {
      setAge('');
    }
  }, [patientInfo.dob]);

  // Calculate years since onset for each allergy
  useEffect(() => {
    const years = allergies.map(allergy => {
      if (allergy.onset_date) {
        const onset = dayjs(allergy.onset_date);
        const today = dayjs();
        const diff = today.diff(onset, 'year');
        return isNaN(diff) ? '' : diff;
      }
      return '';
    });
    setAllergyYears(years);
  }, [allergies]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate patient info
    if (!isPatientInfoFilled) {
      toast.warn("Please fill all patient information fields");
      return;
    }
    setLoading(true);
    try {
      const patientData = {
        first_name: patientInfo.firstName,
        last_name: patientInfo.lastName,
        date_of_birth: patientInfo.dob,
        gender: patientInfo.gender,
        phone_number: patientInfo.phone,
        id_number: patientInfo.idNumber,
        id_type: patientInfo.idType,
        address: patientInfo.address,
      };
      const patientResult = await dispatch(addPatient(patientData));

      if (addPatient.rejected.match(patientResult)) {
        if (patientResult.payload && patientResult.payload.toLowerCase().includes('id number')) {
          toast.error('ID Number already exists. Please use a different ID.');
          setLoading(false);
          return;
        }
      }

      if (addPatient.fulfilled.match(patientResult)) {
        const patientId = patientResult?.payload?.data?.id;

        // If allergies are filled or no known allergies is checked, add them
        if (isAllergiesFilled) {
          if (noKnownAllergies) {
            // Now that we've updated the backend to use a valid date
            // Send a complete object with all fields
            const currentDate = new Date().toISOString().split("T")[0]; // Format: YYYY-MM-DD
            await dispatch(
              addPatientAllergy({
                allergies: [
                  {
                    no_known_allergies: true,
                    // These values don't matter much as they'll be replaced by '--' on the server
                    // But they need to pass validation
                    name: "No Known Allergies",
                    reaction: "None",
                    onset_date: currentDate,
                  },
                ],
                patientId,
              })
            );
          } else {
            // Send the filled allergies
            const allergiesWithPatientId = allergies.map(allergy => ({
              ...allergy,
              patient_id: patientId
            }));
            await dispatch(addPatientAllergy({ allergies: allergiesWithPatientId, patientId }));
          }
        }

        // Reset all form fields
        setPatientInfo({
          firstName: "",
          lastName: "",
          dob: "",
          gender: "",
          address: "",
          phone: "",
          idType: "",
          idNumber: "",
        });

        setAllergies([
          {
            name: "",
            reaction: "",
            onset_date: "",
          },
        ]);

        // Reset no known allergies checkbox
        setNoKnownAllergies(false);

        toast.success("Patient added successfully");
      }
    } catch (error) {
      console.error("Failed to add patient:", error);

      // Handling Redux thunk errors with more detail
      if (error.payload) {
        // This is a rejected action payload
        const errorData = error.payload;
        let errorMessage = "Failed to add patient";

        if (errorData.data?.error) {
          errorMessage += `: ${errorData.data.error}`;
        } else if (errorData.message) {
          errorMessage += `: ${errorData.message}`;
        }

        toast.error(errorMessage);
      }
      // Handling regular errors
      else if (error.response && error.response.data) {
        toast.error(
          `Failed to add patient: ${error.response.data.message || JSON.stringify(error.response.data)
          }`
        );
      } else {
        toast.error("Failed to add patient");
      }
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbItems = [
    {
      label: "Prescriptions",
      onClick: () => navigate('/retail/prescriptions'),
    },
    {
      label: "Patients info",
      onClick: () => navigate(-1),
    },
    {
      label: "Add patient",
    },
  ];

  return (
    <div className="w-full bg-white font-lufga">
      <div className="flex flex-col justify-between mx-8 mb-2 ga">
        <p className="text-xl font-semibold lg:text-3xl text-slate-600">
          {" "}
          Patient Form
        </p>
        <Breadcrumb
          items={breadcrumbItems}
          className="flex items-center text-sm text-green-500 transition-all duration-300 hover:text-green-600"
        />
      </div>
      <form className="p-2 mx-auto sm:p-8 ">
        <div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="firstName"
                value={patientInfo.firstName}
                onChange={(e) =>
                  handlePatientInfoChange("firstName", e.target.value)
                }
                className="w-full px-2 py-1 border rounded focus:border-green-500 focus:ring-green-500 focus:outline-none"
              />
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="lastName"
                value={patientInfo.lastName}
                onChange={(e) =>
                  handlePatientInfoChange("lastName", e.target.value)
                }
                className="w-full px-2 py-1 border rounded focus:border-green-500 focus:ring-green-500 focus:outline-none"
              />
            </div>
            <div className="flex items-center gap-1">
              <div className="w-10/12">
                <label htmlFor="dob" className="block text-sm font-medium">
                  Date of Birth <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  id="dob"
                  max={new Date().toISOString().split("T")[0]}
                  value={patientInfo.dob}
                  onChange={(e) => handlePatientInfoChange("dob", e.target.value, 'Future dates are not allowed')}
                  className="w-full px-2 py-1 border rounded focus:border-green-500 focus:ring-green-500 focus:outline-none"
                />
              </div>
              <div className="w-2/12">
                <label htmlFor="age" className="block text-sm font-medium">
                  Age <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="age"
                  value={age}
                  readOnly
                  className="w-full px-2 py-1 bg-gray-100 border rounded focus:border-green-500 focus:ring-green-500 focus:outline-none"
                />
              </div>
            </div>
            <div>
              <label htmlFor="gender" className="block text-sm font-medium">
                Gender <span className="text-red-500">*</span>
              </label>
              <select
                className="w-full p-2 border rounded focus:border-green-500 focus:ring-green-500 focus:outline-none"
                value={patientInfo.gender}
                onChange={(e) =>
                  handlePatientInfoChange("gender", e.target.value)
                }
                required
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <div>
              <label htmlFor="address" className="block text-sm font-medium">
                Patient Address <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="address"
                value={patientInfo.address}
                onChange={(e) =>
                  handlePatientInfoChange("address", e.target.value)
                }
                className="w-full px-2 py-1 border rounded focus:border-green-500 focus:ring-green-500 focus:outline-none"
              />
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-medium">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                value={patientInfo.phone}
                onChange={(e) =>
                  handlePatientInfoChange("phone", e.target.value)
                }
                className="w-full px-2 py-1 border rounded focus:border-green-500 focus:ring-green-500 focus:outline-none"
              />
            </div>

            <div>
              <label htmlFor="idType" className="block text-sm font-medium">
                ID Type <span className="text-red-500">*</span>
              </label>
              <select
                className="w-full p-1.5 border rounded focus:border-green-500 focus:ring-green-500 focus:outline-none"
                value={patientInfo.idType}
                onChange={(e) =>
                  handlePatientInfoChange("idType", e.target.value)
                }
                required
              >
                <option value="">Select ID type</option>
                <option value="Ghana Card">Ghana Card</option>
                <option value="Health Insurance">Health Insurance</option>
                <option value="Passport">Passport</option>
                <option value="Driver's license">Driver's license</option>
              </select>
            </div>
            <div>
              <label htmlFor="idNumber" className="block text-sm font-medium">
                ID Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="idNumber"
                value={patientInfo.idNumber}
                onChange={(e) =>
                  handlePatientInfoChange("idNumber", e.target.value)
                }
                className="w-full px-2 py-1 border rounded"
              />
            </div>
          </div>

          <div
            className={`sm:p-4 border-b sm:shadow rounded-md mt-4 ${!isPatientInfoFilled ? "opacity-50 pointer-events-none" : ""
              }`}
          >
            <h2 className="mb-2 text-lg font-medium">Allergies </h2>

            <div className="mb-4">
              <label className="flex items-center space-x-2 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={noKnownAllergies}
                  onChange={handleNoKnownAllergiesChange}
                  className="w-5 h-5 text-green-500 form-checkbox"
                />
                <span>No Known Allergies</span>
              </label>
            </div>

            {!noKnownAllergies && (
              <>
                {allergies.map((allergy, index) => (
                  <div
                    key={index}
                    className="relative p-2 mb-4 border rounded sm:p-4"
                  >
                    {allergies.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeAllergy(index)}
                        className="absolute px-2 py-1 text-xs text-white bg-red-500 rounded top-2 right-2 hover:bg-red-600"
                      >
                        Remove
                      </button>
                    )}
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <label
                          htmlFor={`allergy-name-${index}`}
                          className="block text-sm font-medium"
                        >
                          Allergy name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id={`allergy-name-${index}`}
                          value={allergy.name}
                          onChange={(e) =>
                            handleAllergyChange(index, "name", e.target.value)
                          }
                          className="w-full px-2 py-1 border rounded focus:border-green-500 focus:ring-green-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor={`allergy-reaction-${index}`}
                          className="block text-sm font-medium"
                        >
                          Reaction <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id={`allergy-reaction-${index}`}
                          value={allergy.reaction}
                          onChange={(e) =>
                            handleAllergyChange(
                              index,
                              "reaction",
                              e.target.value
                            )
                          }
                          className="w-full px-2 py-1 border rounded focus:border-green-500 focus:ring-green-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor={`allergy-onset_date-${index}`}
                          className="block text-sm font-medium"
                        >
                          Onset Date <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          id={`allergy-onset_date-${index}`}
                          value={allergy.onset_date}
                          onChange={(e) =>
                            handleAllergyChange(
                              index,
                              "onset_date",
                              e.target.value
                            )
                          }
                          className="w-full px-2 py-1 border rounded focus:border-green-500 focus:ring-green-500 focus:outline-none"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                <div className="flex mt-2 space-x-2">
                  <button
                    type="button"
                    onClick={addAllergy}
                    className="flex items-center bg-green-600 hover:bg-green-700 text-white py-1.5 px-4 rounded-md shadow-sm text-sm font-semibold transition-colors duration-150"
                  >
                    <span className="mr-2 text-lg font-bold">+</span> Add Another Allergy
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button
            type="button"
            onClick={handleSubmit}
            className="px-4 py-1 text-sm text-white bg-green-500 rounded-md shadow-sm hover:opacity-90"
            disabled={loading}
          >
            {loading ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddPatient;
